import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icons from '../components/common/Icons';

const benefits = [
  { icon: <Icons.Wind />, title: 'Oxygen Production', text: 'A single mature tree produces approximately 100 kg of oxygen per year, enough for two people to breathe.', stat: '100 kg/year', statLabel: 'per mature tree' },
  { icon: <Icons.Cloud />, title: 'Carbon Capture', text: 'Each tree absorbs about 22 kg of CO₂ annually, locking away carbon that would otherwise warm our planet.', stat: '22 kg CO₂', statLabel: 'absorbed per year' },
  { icon: <Icons.Thermometer />, title: 'Temperature Regulation', text: 'Trees can reduce surrounding temperature by 2-8 degrees Celsius through shade and evapotranspiration.', stat: '2-8°C', statLabel: 'temperature reduction' },
  { icon: <Icons.Droplet />, title: 'Water Cycle', text: 'A large tree releases up to 400 litres of water vapour per day, replenishing atmospheric moisture and rainfall.', stat: '400 litres', statLabel: 'water vapour per day' },
  { icon: <Icons.Bug />, title: 'Biodiversity Habitat', text: 'A single tree can be home to hundreds of species of insects, birds, fungi, and microorganisms.', stat: '100+', statLabel: 'species per tree' },
  { icon: <Icons.Filter />, title: 'Clean Air', text: 'Trees filter harmful pollutants like NO₂, SO₂, and particulate matter, improving the air quality around us.', stat: '1.7 kg', statLabel: 'pollutants filtered/year' },
];

const funFacts = [
  'One acre of trees absorbs as much CO₂ in a year as a car driven 42,000 km.',
  'Trees in a city reduce the need for air conditioning by up to 30%.',
  'India\'s forest cover is approximately 7,13,789 sq km — about 21.71% of the total area.',
  'A single banyan tree in Kolkata has over 3,300 aerial prop roots.',
];

export default function TreeBenefitsPage() {
  useEffect(() => {
    document.title = 'Why Trees | Haritanjali';
  }, []);

  return (
    <>
      {/* Hero */}
      <section style={{
        position: 'relative',
        height: '45vh',
        minHeight: 340,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/images/climate-change.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(27,94,32,0.75) 100%)',
        }} />
        <div style={{
          position: 'relative', zIndex: 2, textAlign: 'center',
          color: '#fff', maxWidth: 700, padding: '0 20px',
        }}>
          <p style={{
            fontSize: 14, letterSpacing: 4, textTransform: 'uppercase',
            marginBottom: 16, opacity: 0.85, fontWeight: 500,
          }}>
            The Power of Trees
          </p>
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 56px)',
            fontWeight: 800, lineHeight: 1.12,
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
          }}>
            What Trees Do For You
          </h1>
          <p style={{
            fontSize: 'clamp(15px, 2vw, 19px)',
            opacity: 0.9, maxWidth: 550, margin: '16px auto 0',
            lineHeight: 1.7,
          }}>
            Every tree is a life-sustaining powerhouse working silently for the planet.
          </p>
        </div>
      </section>

      {/* Benefits grid */}
      <section style={{ padding: '80px 20px', background: '#f5f7f5' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 24,
          }}>
            {benefits.map((b, i) => (
              <div
                key={i}
                style={{
                  background: '#fff', borderRadius: 16, padding: '36px 28px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  border: '1px solid rgba(46,125,50,0.08)',
                  transition: 'all 0.35s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: '#e8f5e9', color: '#2e7d32',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {b.icon}
                  </div>
                  <div>
                    <h3 style={{ color: '#1b5e20', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{b.title}</h3>
                    <div style={{
                      display: 'inline-block',
                      background: '#e8f5e9', color: '#2e7d32',
                      borderRadius: 12, padding: '2px 10px',
                      fontSize: 12, fontWeight: 700,
                    }}>
                      {b.stat} &mdash; {b.statLabel}
                    </div>
                  </div>
                </div>
                <p style={{ color: '#666', lineHeight: 1.7, fontSize: 15 }}>{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fun Facts */}
      <section style={{
        padding: '60px 20px',
        background: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
        textAlign: 'center',
      }}>
        <h2 style={{ color: '#fff', fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, marginBottom: 40 }}>
          Did You Know?
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 20, maxWidth: 1100, margin: '0 auto',
        }}>
          {funFacts.map((fact, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 14, padding: '24px 20px',
              color: 'rgba(255,255,255,0.9)', fontSize: 15, lineHeight: 1.7,
            }}>
              <span style={{ fontSize: 24, marginRight: 8 }}>🌳</span>
              {fact}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '70px 20px', background: '#fff', textAlign: 'center' }}>
        <h2 style={{
          fontSize: 'clamp(26px, 4vw, 40px)',
          color: '#1b5e20', fontWeight: 800, marginBottom: 16,
        }}>
          Plant a Tree. Change a Life.
        </h2>
        <p style={{
          fontSize: 17, color: '#666',
          maxWidth: 500, margin: '0 auto 32px', lineHeight: 1.7,
        }}>
          Join thousands of individuals and organisations planting trees across India.
        </p>
        <Link to="/register" style={{
          display: 'inline-block',
          background: '#ff9800', color: '#fff',
          border: 'none', padding: '14px 40px',
          fontSize: 16, fontWeight: 700,
          borderRadius: 50,
          boxShadow: '0 4px 20px rgba(255,152,0,0.4)',
          textDecoration: 'none',
        }}>
          Start Planting Today
        </Link>
      </section>
    </>
  );
}
